import { createStore } from '~/state/createStore';

export {
  ACCOUNT_MODAL_MODULE,
  CHAT_UI_MODULE
} from '~/constants/common/MfeModules';

export type MfeLoadEvent = {
  componentName: string;
  // 'loaded'  — bundle registered in globalThis (framework-emitted, too early for interactions)
  // 'ready'   — MFE app initialized and safe to call (application-emitted)
  // 'teardown'— MFE app unmounting (application-emitted, pre-REO-8289 framework support)
  data: 'error' | 'loaded' | 'ready' | 'teardown' | 'timeout';
  moduleName: string;
  timestamp: number;
};

type MfeModuleState = {
  isLoaded: boolean;
  isReady: boolean;
  loadCount: number;
};

export type MfeLoadState = {
  modules: Record<string, MfeModuleState>;
  record: (event: MfeLoadEvent) => void;
};

/**
 * Zustand store that tracks MFE lifecycle state, keyed by module name.
 * Populated by MfeEventsListener, which must be mounted in the page's layout. Ready/unready
 * signals are declared per MFE in MFE_DEFINITIONS (~/constants/common/MfeModules).
 *
 * Pages with MFE load state available (MfeEventsListener is in their layout):
 *   /cars, /vehicle, /financing-rsc, /shareyourcarvana  — all under (carvana-layout)
 *
 * Pages WITHOUT MFE load state (MfeEventsListener not present):
 *   / (Home, Pages Router), /(financing), /dealership, /vending-machine
 *
 * Read readiness through useMfeReady rather than this store directly — it also listens for
 * the MFE's ready event itself, so it still works on pages without MfeEventsListener.
 */
export const useMfeLoadStore = createStore<MfeLoadState>(
  'MfeLoadState',
  set => ({
    modules: {},
    record: event =>
      set(state => {
        const prev = state.modules[event.moduleName] ?? {
          isLoaded: false,
          isReady: false,
          loadCount: 0
        };

        return {
          modules: {
            ...state.modules,
            [event.moduleName]: {
              isLoaded: prev.isLoaded || event.data === 'loaded',
              isReady:
                event.data === 'ready'
                  ? true
                  : event.data === 'teardown'
                  ? false
                  : prev.isReady,
              loadCount: prev.loadCount + (event.data === 'loaded' ? 1 : 0)
            }
          }
        };
      })
  })
);

export const selectIsMfeLoaded =
  (moduleName: string) => (state: MfeLoadState) =>
    state.modules[moduleName]?.isLoaded ?? false;

export const selectIsMfeReady = (moduleName: string) => (state: MfeLoadState) =>
  state.modules[moduleName]?.isReady ?? false;

export const selectMfeLoadCount =
  (moduleName: string) => (state: MfeLoadState) =>
    state.modules[moduleName]?.loadCount ?? 0;
